import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Clock, X, ChatCircle, ArrowRight, User } from '@phosphor-icons/react';
import { listAwaiting, withdrawAllocation, type Allocation } from '@/lib/allocationsApi';
import { getApiErrorMessage } from '@/lib/api';
import { onAllocationUpdate, useWebSocketStore } from '@/lib/websocket';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Clerk dashboard widget — every brief currently awaiting a barrister
 * response (status pending or conference_requested).
 */
/**
 * @param basePath the viewer's own area. This tray renders on the ADMIN dashboard as well
 *   as the clerk's, but hardcoded `/clerk/matters/:id` — a route guarded to clerks — so
 *   two of its only interactive elements were inert for admins.
 */
export function AwaitingResponseTray({ basePath = '/clerk' }: { basePath?: string } = {}) {
  const [rows, setRows] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const wsConnected = useWebSocketStore((s) => s.connected);

  const reload = () => {
    setLoading(true);
    listAwaiting()
      .then(setRows)
      .catch((err) => setError(getApiErrorMessage(err, 'Could not load tray')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling + WebSocket subscription — re-attaches when socket connects/reconnects.
  useEffect(() => {
    const interval = setInterval(reload, 300_000);
    const unsubWs = onAllocationUpdate(() => reload());
    return () => {
      clearInterval(interval);
      unsubWs();
    };
  }, [wsConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWithdraw = async (id: string) => {
    if (!confirm('Withdraw this offer? You can re-allocate to another barrister afterwards.'))
      return;
    try {
      await withdrawAllocation(id);
      toast.success('Offer withdrawn');
      reload();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not withdraw offer'));
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-[15px]">
            <Briefcase className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="duotone" />
            Awaiting response
            {rows.length > 0 && (
              <span className="ml-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700 dark:text-brand-300 ring-1 ring-brand-200">
                {rows.length}
              </span>
            )}
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Briefs offered to barristers, waiting for them to accept, decline or request a conference.
          </p>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : error ? (
          <div className="mx-5 my-4 rounded-md border border-danger/30 bg-danger-bg px-3 py-2 text-xs text-danger">
            {error}
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">
            No open offers. When you offer a brief from a case page, it will appear here until the
            barrister responds.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((a) => {
              const barName = a.barrister?.user
                ? `${a.barrister.user.firstName} ${a.barrister.user.lastName}`
                : 'Unknown barrister';
              const initials = a.barrister?.user
                ? `${a.barrister.user.firstName?.[0] ?? ''}${a.barrister.user.lastName?.[0] ?? ''}`
                : '?';
              const memberType = a.barrister?.user?.memberType ?? '';
              const rankLabel =
                memberType === 'silk'
                  ? "King's Counsel"
                  : memberType === 'junior'
                    ? 'Junior Barrister'
                    : memberType;
              const isCon = a.status === 'conference_requested';

              return (
                <li key={a.id} className="px-5 py-4">
                  <div className="flex items-start gap-3">
                    {/* Avatar with initials — makes barrister identity unmistakable */}
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700 dark:text-brand-300 ring-1 ring-brand-200">
                      {initials.toUpperCase() || <User className="h-4 w-4" />}
                    </div>

                    <div className="min-w-0 flex-1">
                      {/* Top line — STATUS + barrister name (the headline) */}
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.06em] ring-1 ring-inset ${
                            isCon
                              ? 'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 ring-amber-200 dark:ring-amber-500/30'
                              : 'bg-brand-50 text-brand-700 dark:text-brand-300 ring-brand-200'
                          }`}
                        >
                          <Clock className="h-2.5 w-2.5" weight="fill" />
                          {isCon ? 'Conference requested' : 'Awaiting reply'}
                        </span>
                        <span className="text-[15px] font-semibold tracking-tight text-foreground">
                          {barName}
                        </span>
                        {rankLabel && (
                          <span className="text-xs text-muted-foreground">
                            · {rankLabel}
                          </span>
                        )}
                      </div>

                      {/* Second line — case + when */}
                      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
                        <span>Brief on</span>
                        <Link
                          to={`${basePath}/matters/${a.case.id}`}
                          className="truncate font-medium text-foreground hover:text-brand-700 dark:hover:text-brand-300"
                        >
                          {a.case.caseName}
                        </Link>
                        <span className="text-xs text-muted-foreground">
                          {a.case.caseNumber}
                        </span>
                        <span>·</span>
                        <span>offered {timeAgo(a.offeredAt)}</span>
                      </div>

                      {/* Conference badge if applicable */}
                      {isCon && a.requestedConference && (
                        <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10 px-2 py-1 text-xs text-amber-800 dark:text-amber-300">
                          <ChatCircle className="h-3 w-3" weight="fill" />
                          <span>{describeConference(a.requestedConference)}</span>
                        </div>
                      )}

                      {/* Clerk's note */}
                      {a.clerkNote && (
                        <div className="mt-1.5 truncate text-xs italic text-muted-foreground">
                          “{a.clerkNote}”
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-shrink-0 items-center gap-1">
                      <Link
                        to={`${basePath}/matters/${a.case.id}`}
                        className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium text-foreground transition hover:bg-muted"
                      >
                        Open <ArrowRight className="h-3 w-3" />
                      </Link>
                      <button
                        onClick={() => handleWithdraw(a.id)}
                        title="Withdraw offer"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition hover:border-danger/40 hover:bg-danger-bg hover:text-danger"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}

function describeConference(c: {
  purpose: string;
  format: string;
  layClientAttending: boolean;
}): string {
  return `${labels.purpose[c.purpose] ?? c.purpose} · ${labels.format[c.format] ?? c.format} · ${
    c.layClientAttending ? 'with lay client' : 'no client'
  }`;
}

const labels: { purpose: Record<string, string>; format: Record<string, string> } = {
  purpose: {
    screening: 'Screening',
    case_prep: 'Case prep',
    court_day: 'Court-day',
    settlement: 'Settlement',
    post_trial: 'Post-trial',
    other: 'Other',
  },
  format: {
    in_chambers: 'In chambers',
    at_court: 'At court',
    video: 'Video',
    phone: 'Phone',
  },
};
